import React from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
} from 'recharts';

import { CustomerData, CustomerDataRow } from '../../customer-data/api';
import { ConversionType } from './UnitToggle';
import { CONVERSION_DIVISORS, formatCurrency } from './format';

const MONTHS = [
    'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
    'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar',
] as const;

type MonthKey = (typeof MONTHS)[number];

interface ChartPoint {
    month: MonthKey;
    income: number;
    expenses: number;
    contribution: number;
}

interface Props {
    customer: CustomerData;
    conversionType: ConversionType;
}

function rowByCategory(rows: CustomerDataRow[], category: string): CustomerDataRow | undefined {
    return rows.find((r) => r.Category === category);
}

function buildChartData(customer: CustomerData): ChartPoint[] {
    const income = rowByCategory(customer.rows, 'INCOME');
    const expenses = rowByCategory(customer.rows, 'EXPENSES');
    const cm = rowByCategory(customer.rows, 'CM');

    return MONTHS.map((month) => ({
        month,
        income: income?.[month] ?? 0,
        expenses: expenses?.[month] ?? 0,
        contribution: cm?.[month] ?? 0,
    }));
}

const ProfitabilityChart: React.FC<Props> = ({ customer, conversionType }) => {
    const data = React.useMemo(() => buildChartData(customer), [customer]);

    const hasData = data.some(
        (d) => d.income !== 0 || d.expenses !== 0 || d.contribution !== 0
    );

    const unitLabel = conversionType === 'INR' ? 'INR' : conversionType;
    const divisor = CONVERSION_DIVISORS[conversionType];

    const formatAxisValue = (value: number) => {
        const display = value / divisor;
        if (Math.abs(display) >= 100) {
            return display.toLocaleString('en-IN', { maximumFractionDigits: 0 });
        }
        return display.toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
        });
    };

    if (!hasData) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
                No monthly profitability data to chart.
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-baseline justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-800">
                    Monthly Profitability
                </h3>
                <span className="text-xs text-gray-500">Values in {unitLabel}</span>
            </div>

            <div className="h-[320px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />

                        <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12, fill: '#6b7280' }}
                            axisLine={{ stroke: '#d1d5db' }}
                            tickLine={false}
                        />

                        <YAxis
                            tick={{ fontSize: 12, fill: '#6b7280' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatAxisValue}
                            width={56}
                        />

                        <Tooltip
                            contentStyle={{
                                borderRadius: 8,
                                border: '1px solid #e5e7eb',
                                fontSize: 12,
                            }}
                            formatter={(value, name) => [
                                formatCurrency(Number(value ?? 0), conversionType),
                                name,
                            ]}
                            labelFormatter={(label) => String(label)}
                        />

                        <Legend
                            verticalAlign="top"
                            align="right"
                            wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
                        />

                        <Bar
                            dataKey="income"
                            name="Income"
                            fill="#2563eb"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={28}
                        />

                        <Bar
                            dataKey="expenses"
                            name="Expenses"
                            fill="#64748b"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={28}
                        />

                        <Line
                            type="monotone"
                            dataKey="contribution"
                            name="Contribution"
                            stroke="#16a34a"
                            strokeWidth={2.5}
                            dot={{ r: 3, fill: '#16a34a', strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ProfitabilityChart;
